import React, { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { ArrowLeft, Trash2, Loader2, Mail, Phone, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { BRANCHES } from "@/lib/brand";

const STATUSES = [
  { v: "new", label: "Nouveau" },
  { v: "in_progress", label: "En cours" },
  { v: "done", label: "Traité" },
  { v: "archived", label: "Archivé" },
];

function statusColor(s) {
  switch (s) {
    case "new": return "bg-white/10 text-white";
    case "in_progress": return "bg-amber-500/10 text-amber-400 border-amber-500/30";
    case "done": return "bg-emerald-500/10 text-emerald-400 border-emerald-500/30";
    case "archived": return "bg-zinc-500/10 text-zinc-400";
    default: return "bg-white/10 text-white";
  }
}

export default function AdminContactRequests() {
  const { user, ready } = useAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const fetchAll = async () => {
    try {
      const { data } = await api.get("/admin/contact-requests");
      setList(data);
    } catch {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (ready && user) fetchAll();
  }, [ready, user]);

  if (ready === false) return null;
  if (ready && !user) return <Navigate to="/admin/login" replace />;

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/contact-requests/${id}`, { status });
      setList((l) => l.map((r) => (r.id === id ? { ...r, status } : r)));
      if (selected?.id === id) setSelected({ ...selected, status });
      toast.success("Statut mis à jour");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  const removeOne = async (id) => {
    if (!window.confirm("Supprimer cette demande ?")) return;
    try {
      await api.delete(`/admin/contact-requests/${id}`);
      setList((l) => l.filter((r) => r.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success("Supprimée");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  return (
    <div className="pt-28 pb-24 min-h-screen" data-testid="page-contact-requests">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-zinc-500 hover:text-white mb-6"
          data-testid="requests-back-admin"
        >
          <ArrowLeft size={14} /> Retour au tableau de bord
        </Link>

        <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">Gabriel</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mt-2">
          <MessageSquare className="inline -mt-1 mr-2" size={32} /> Messages reçus
        </h1>
        <p className="mt-2 text-zinc-400 text-sm">
          Demandes de rappel transmises par Gabriel (chatbot du site).
        </p>

        <div className="mt-8 rounded-xl border border-white/10 overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-zinc-400">
              <Loader2 className="inline animate-spin mr-2" /> Chargement…
            </div>
          ) : list.length === 0 ? (
            <div className="p-12 text-center text-zinc-500" data-testid="requests-empty">
              Aucun message reçu pour le moment.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="requests-table">
                <thead className="bg-[#111] text-zinc-500 uppercase text-xs tracking-wider">
                  <tr>
                    <th className="text-left px-5 py-3">Date</th>
                    <th className="text-left px-5 py-3">Contact</th>
                    <th className="text-left px-5 py-3">Pôle</th>
                    <th className="text-left px-5 py-3">Client ?</th>
                    <th className="text-left px-5 py-3">Statut</th>
                    <th className="text-right px-5 py-3">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((r) => {
                    const br = r.branch ? BRANCHES[r.branch] : null;
                    return (
                      <tr
                        key={r.id}
                        className="border-t border-white/5 hover:bg-white/[0.03] cursor-pointer"
                        onClick={() => setSelected(r)}
                        data-testid={`request-row-${r.id}`}
                      >
                        <td className="px-5 py-3 text-zinc-400 whitespace-nowrap">
                          {new Date(r.created_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "2-digit" })}
                        </td>
                        <td className="px-5 py-3">
                          <div className="font-medium">{r.name}</div>
                          <div className="text-xs text-zinc-500">{r.email || r.phone || "—"}</div>
                        </td>
                        <td className="px-5 py-3">
                          {br ? (
                            <span
                              className="px-2 py-1 rounded-full text-xs font-medium border"
                              style={{ color: br.accent, borderColor: br.accent + "55", backgroundColor: br.accent + "15" }}
                            >
                              {br.short}
                            </span>
                          ) : <span className="text-zinc-500">—</span>}
                        </td>
                        <td className="px-5 py-3">
                          {r.is_existing_customer ? (
                            <span className="text-emerald-400 text-xs">Oui</span>
                          ) : (
                            <span className="text-zinc-500 text-xs">Non</span>
                          )}
                        </td>
                        <td className="px-5 py-3">
                          <span className={`px-2 py-1 rounded-full text-xs border ${statusColor(r.status)}`}>
                            {STATUSES.find((s) => s.v === r.status)?.label || r.status}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-right">
                          <button
                            onClick={(e) => { e.stopPropagation(); removeOne(r.id); }}
                            data-testid={`request-delete-${r.id}`}
                            className="p-2 rounded hover:bg-white/10 text-zinc-400 hover:text-rose-400"
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 flex" data-testid="request-drawer">
          <div className="flex-1 bg-black/70 backdrop-blur-sm" onClick={() => setSelected(null)} />
          <aside className="w-full sm:max-w-xl bg-[#0e0e0e] border-l border-white/10 overflow-y-auto">
            <div className="p-8">
              <button
                onClick={() => setSelected(null)}
                className="text-xs uppercase tracking-[0.2em] text-zinc-500 hover:text-white"
              >
                ← Fermer
              </button>
              <h3 className="font-display text-3xl font-semibold mt-4">{selected.name}</h3>
              <div className="mt-2 text-sm text-zinc-400 flex flex-wrap gap-4">
                {selected.email && (
                  <a href={`mailto:${selected.email}`} className="inline-flex items-center gap-2">
                    <Mail size={14} /> {selected.email}
                  </a>
                )}
                {selected.phone && (
                  <a href={`tel:${selected.phone}`} className="inline-flex items-center gap-2">
                    <Phone size={14} /> {selected.phone}
                  </a>
                )}
              </div>

              <div className="mt-6">
                <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">Résumé</div>
                <div className="text-sm text-zinc-200 whitespace-pre-wrap rounded-md bg-[#141414] border border-white/10 p-4">
                  {selected.summary}
                </div>
              </div>

              <div className="mt-6 grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">Pôle</div>
                  <div className="mt-1">{selected.branch ? BRANCHES[selected.branch]?.name : "—"}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">Client existant</div>
                  <div className="mt-1">{selected.is_existing_customer ? "Oui" : "Non / Inconnu"}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">Reçue le</div>
                  <div className="mt-1">{new Date(selected.created_at).toLocaleString("fr-FR")}</div>
                </div>
              </div>

              <div className="mt-8">
                <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-3">Statut</div>
                <div className="flex flex-wrap gap-2">
                  {STATUSES.map((s) => (
                    <button
                      key={s.v}
                      onClick={() => updateStatus(selected.id, s.v)}
                      className={`px-3 py-1.5 rounded-md text-xs border ${
                        selected.status === s.v ? "bg-white text-black border-white" : "border-white/15 text-zinc-300 hover:bg-white/5"
                      }`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
